import { Question, QuestionCore, ModuleId } from '../types'
import { MODULES } from './modules'

// Import all questions with error handling
let METRIEK_QUESTIONS: QuestionCore[] = []
let GETALBEGRIP_QUESTIONS: QuestionCore[] = []
let TIJDGELD_QUESTIONS: QuestionCore[] = []
let SPELLINGWW_QUESTIONS: QuestionCore[] = []
let SPELLINGREGEL_QUESTIONS: QuestionCore[] = []
let TAALBEGRIP_QUESTIONS: QuestionCore[] = []

try {
  METRIEK_QUESTIONS = require('./questions/metriek').METRIEK_QUESTIONS || []
  GETALBEGRIP_QUESTIONS =
    require('./questions/getalbegrip').GETALBEGRIP_QUESTIONS || []
  TIJDGELD_QUESTIONS = require('./questions/tijdgeld').TIJDGELD_QUESTIONS || []
  SPELLINGWW_QUESTIONS =
    require('./questions/spellingww').SPELLINGWW_QUESTIONS || []
  SPELLINGREGEL_QUESTIONS =
    require('./questions/spellingregel').SPELLINGREGEL_QUESTIONS || []
  TAALBEGRIP_QUESTIONS =
    require('./questions/taalbegrip').TAALBEGRIP_QUESTIONS || []
} catch (error) {
  console.error('❌ Error loading question files:', error)
}

// Import all explanations with error handling
let METRIEK_EXPLANATIONS: any = {}
let GETALBEGRIP_EXPLANATIONS: any = {}
let TIJDGELD_EXPLANATIONS: any = {}
let SPELLINGWW_EXPLANATIONS: any = {}
let SPELLINGREGEL_EXPLANATIONS: any = {}
let TAALBEGRIP_EXPLANATIONS: any = {}

try {
  const explanations = require('./explanations')
  METRIEK_EXPLANATIONS = explanations.METRIEK_EXPLANATIONS || {}
  GETALBEGRIP_EXPLANATIONS = explanations.GETALBEGRIP_EXPLANATIONS || {}
  TIJDGELD_EXPLANATIONS = explanations.TIJDGELD_EXPLANATIONS || {}
  SPELLINGWW_EXPLANATIONS = explanations.SPELLINGWW_EXPLANATIONS || {}
  SPELLINGREGEL_EXPLANATIONS = explanations.SPELLINGREGEL_EXPLANATIONS || {}
  TAALBEGRIP_EXPLANATIONS = explanations.TAALBEGRIP_EXPLANATIONS || {}
} catch (error) {
  console.error('❌ Error loading explanation files:', error)
}

// Combine all questions (without explanations) - for Cito
export const ALL_QUESTIONS_CORE: QuestionCore[] = [
  ...METRIEK_QUESTIONS,
  ...GETALBEGRIP_QUESTIONS,
  ...TIJDGELD_QUESTIONS,
  ...SPELLINGWW_QUESTIONS,
  ...SPELLINGREGEL_QUESTIONS,
  ...TAALBEGRIP_QUESTIONS,
]

// Combine all explanations
const ALL_EXPLANATIONS = {
  ...METRIEK_EXPLANATIONS,
  ...GETALBEGRIP_EXPLANATIONS,
  ...TIJDGELD_EXPLANATIONS,
  ...SPELLINGWW_EXPLANATIONS,
  ...SPELLINGREGEL_EXPLANATIONS,
  ...TAALBEGRIP_EXPLANATIONS,
}

/**
 * Get a single question with its explanation
 */
export function getQuestionWithExplanation(
  questionId: string,
): Question | null {
  try {
    const core = ALL_QUESTIONS_CORE.find((q) => q.id === questionId)
    if (!core) {
      console.error(`❌ QUESTION ERROR: Question not found: ${questionId}`)
      return null
    }

    const explanation = ALL_EXPLANATIONS[questionId]
    if (!explanation) {
      console.warn(`⚠️ MISSING EXPLANATION: ${questionId} (${core.module})`)
      // Return question without explanation if not found
      return {
        ...core,
        explanation: 'Uitleg wordt binnenkort toegevoegd.',
        distractorExplanations: {},
      }
    }

    // Validate explanation structure
    if (!explanation.explanation) {
      console.warn(
        `⚠️ INCOMPLETE EXPLANATION: ${questionId} - missing main explanation`,
      )
    }

    if (
      !explanation.distractorExplanations ||
      Object.keys(explanation.distractorExplanations).length === 0
    ) {
      console.warn(
        `⚠️ INCOMPLETE EXPLANATION: ${questionId} - missing distractor explanations`,
      )
    }

    // Validate visual aid if present
    if (explanation.visualAid) {
      if (!explanation.visualAid.type) {
        console.error(`❌ VISUAL AID ERROR: ${questionId} - missing type`)
      }
      if (!explanation.visualAid.data) {
        console.error(`❌ VISUAL AID ERROR: ${questionId} - missing data`)
      }
    }

    return {
      ...core,
      explanation: explanation.explanation,
      distractorExplanations: explanation.distractorExplanations,
      visualAid: explanation.visualAid,
    }
  } catch (error) {
    console.error('❌ Error in getQuestionWithExplanation:', error, questionId)
    return null
  }
}

/**
 * Get all questions for a module (with explanations)
 * Used by: PracticeSession, normal practice mode
 */
export function getQuestionsByModule(moduleId: ModuleId): Question[] {
  try {
    const questions = ALL_QUESTIONS_CORE.filter((q) => q.module === moduleId)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)

    if (questions.length === 0) {
      console.warn(`⚠️ No questions found for module: ${moduleId}`)
    }

    return questions
  } catch (error) {
    console.error('❌ Error in getQuestionsByModule:', error, moduleId)
    return []
  }
}

/**
 * Get questions for Cito (without explanations, mixed from all modules)
 * Used by: CitoSimulator
 */
export function getCitoQuestions(count: number = 30): QuestionCore[] {
  try {
    // Mix questions from all modules
    const questionsPerModule = Math.ceil(count / MODULES.length)
    const selected: QuestionCore[] = []

    MODULES.forEach((module) => {
      const moduleQuestions = ALL_QUESTIONS_CORE.filter(
        (q) => q.module === module.id,
      )
        .sort(() => Math.random() - 0.5)
        .slice(0, questionsPerModule)

      selected.push(...moduleQuestions)
    })

    // Shuffle and limit to exact count
    const shuffled = selected.sort(() => Math.random() - 0.5).slice(0, count)

    if (shuffled.length < count) {
      console.warn(
        `⚠️ CITO: Requested ${count} questions, but only ${shuffled.length} available`,
      )
    }

    return shuffled
  } catch (error) {
    console.error('❌ Error in getCitoQuestions:', error)
    return []
  }
}

/**
 * Get hard questions (difficulty 3) across all modules
 * Used by: Superkracht Training (FocusPanel)
 */
export function getHardQuestions(): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter((q) => q.difficulty === 3)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    console.error('❌ Error in getHardQuestions:', error)
    return []
  }
}

/**
 * Get hard questions for a specific module
 * Used by: Module-specific Superkracht Training
 */
export function getHardQuestionsByModule(moduleId: ModuleId): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter(
      (q) => q.module === moduleId && q.difficulty === 3,
    )
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    console.error('❌ Error in getHardQuestionsByModule:', error)
    return []
  }
}

/**
 * Get all questions (with explanations) - backwards compatible
 * Used by: Legacy code that expects full questions
 */
export function getAllQuestions(): Question[] {
  try {
    const questions = ALL_QUESTIONS_CORE.map((q) =>
      getQuestionWithExplanation(q.id),
    ).filter((q): q is Question => q !== null)

    // Log statistics
    const totalQuestions = ALL_QUESTIONS_CORE.length
    const questionsWithExplanations = questions.filter(
      (q) =>
        q.explanation &&
        q.explanation !== 'Uitleg wordt binnenkort toegevoegd.',
    ).length
    const missingExplanations = totalQuestions - questionsWithExplanations

    console.log(`📊 Question Statistics:`)
    console.log(`   Total questions: ${totalQuestions}`)
    console.log(`   With explanations: ${questionsWithExplanations}`)
    console.log(`   Missing explanations: ${missingExplanations}`)

    if (missingExplanations > 0) {
      console.warn(
        `⚠️ ${missingExplanations} questions are missing explanations`,
      )
    }

    return questions
  } catch (error) {
    console.error('❌ Error in getAllQuestions:', error)
    return []
  }
}

/**
 * NIEUW: Get demo questions (for testing new features)
 * Used by: Development and feature demonstration
 */
export function getDemoQuestions(): Question[] {
  // Return empty array - demo questions not implemented yet
  return []
}

/**
 * NIEUW: Get questions by type (for special rendering)
 * Used by: PracticeSession to render special question types
 */
export function getQuestionsByType(type: string): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter((q) => q.questionType === type)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    console.error('❌ Error in getQuestionsByType:', error)
    return []
  }
}

/**
 * NIEUW: Validate all questions and explanations
 * Used by: Development to check data integrity
 */
export function validateQuestionsAndExplanations(): {
  valid: boolean
  errors: string[]
  warnings: string[]
} {
  const errors: string[] = []
  const warnings: string[] = []

  ALL_QUESTIONS_CORE.forEach((question) => {
    // Validate question structure
    if (!question.id) {
      errors.push(`Question missing ID`)
    }
    if (!question.module) {
      errors.push(`Question ${question.id} missing module`)
    }
    if (!question.question) {
      errors.push(`Question ${question.id} missing question text`)
    }
    if (!question.options || question.options.length === 0) {
      errors.push(`Question ${question.id} missing options`)
    }
    if (
      question.correctAnswer === undefined ||
      question.correctAnswer === null
    ) {
      errors.push(`Question ${question.id} missing correctAnswer`)
    }

    // Check for explanation
    const explanation = ALL_EXPLANATIONS[question.id]
    if (!explanation) {
      warnings.push(`Question ${question.id} missing explanation`)
    } else {
      if (!explanation.explanation) {
        warnings.push(`Question ${question.id} missing main explanation text`)
      }
      if (
        !explanation.distractorExplanations ||
        Object.keys(explanation.distractorExplanations).length === 0
      ) {
        warnings.push(`Question ${question.id} missing distractor explanations`)
      }
    }
  })

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

// Export modules for convenience
export { MODULES }
